import type { CustomColumnProps, CustomRowProps } from '../../../components/table/types';
import type { Student } from '@whizz/api-model/dist/@types';
import type Message from '@whizz/api-client/dist/@types/model/Message';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { createGlobalStyle } from 'styled-components';
import { Button, Collapse, Table } from '../../../components';
import { useIdentity } from '@whizz/react-identity';
import { debounce } from 'lodash';
import { getDateStringWithLocale } from '../../../util/dates';

export type MessagesTableAccessors =
  | 'sent'
  | 'body'
  | 'to_multiple';

const GlobalStyle = createGlobalStyle`
  table {
    [data-id='col_body'] {
      width: 100%;
    }
    [data-column='cell_to_multiple'] > * {
      white-space: nowrap;
    }
    tbody {
      td[data-id^='cell'] {
        padding-bottom: 14px;
        vertical-align: top;
      }
    }
  }
`;

interface Props {
  data: Message[];
  scrollContainer?: Window | Element | null;
  stickyTopSelector?: string;
}

const MessagesTable = ({ data, scrollContainer, stickyTopSelector }: Props): JSX.Element => {

  const { me } = useIdentity();
  const [ stickyTop, setStickyTop ] = useState<number | undefined>();
  const { translate, locale } = useI18n();

  const columns = [
    {
      id: 'sent',
      Header: translate('pages.MESSAGES.HEADERS.DATE'),
      accessor: 'sent',
      disableSortBy: true,
    },
    {
      Header: translate('pages.MESSAGES.HEADERS.MESSAGE'),
      accessor: 'body',
      disableSortBy: true,
    },
    {
      Header: translate('pages.MESSAGES.HEADERS.TO'),
      accessor: 'to_multiple',
      disableSortBy: true,
      Cell: ({ row, value }: { row: any, value: Message['to_multiple'] }) => value ? <Students students={value} /> : 'No student',
    },
  ] as CustomColumnProps<MessagesTableAccessors>[];

  const getFormattedData = (): CustomRowProps<MessagesTableAccessors>[]  => {
    return data.map((message) => {
      return {
        sent: getDateStringWithLocale(message.sent!.toJSDate(), locale?.id),
        body: message.body,
        to_multiple: message.to_multiple,
      };
    });
  };

  useEffect(() => {
    const onResize = debounce(() => {
      if (stickyTopSelector) {
        const stickyTopHeight = document.querySelector(stickyTopSelector)?.getBoundingClientRect().height;
        setStickyTop(stickyTopHeight);
      }
    });
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [ stickyTopSelector ]);

  return (
    <React.Fragment>
      <GlobalStyle />
      <div className={'container'}>
        <Table
          id={'messages'}
          showTbodyBorder
          cols={columns}
          content={getFormattedData()}
          noDataMessage={translate('pages.MESSAGES.NO_DATA')}
          scrollContainer={scrollContainer}
          sticky
          stickyTop={stickyTop}
          initialState={{
            sortBy: [],
            pageSize: 20,
          }}
        />
      </div>
    </React.Fragment>
  );
};

const Students = ({ students }: { students: Student[] }) => {
  const { translate } = useI18n();
  const [ open, setOpen ] = useState(false);
  if (students.length > 1) {
    return (
      <>
        <Button asLink onClick={() => setOpen((open) => !open)}>{translate('pages.MESSAGES.STUDENTS', { count: students.length })}</Button>
        <Collapse when={open}>
          <ul className={'ml-2 pt-2 space-y-2'}>
            {students.map((student) => (<li key={`message_student_${student.id}`}>{`${student.getFirstName()} ${student.getLastName()}`}</li>))}
          </ul>
        </Collapse>
      </>
    );
  }
  return <>{`${students[0].getFirstName()} ${students[0].getLastName()}`}</>;
};

MessagesTable.displayName = 'MessagesTable';

export default MessagesTable;
